/**
 * DEN PERSONLIGE KOBLINGEN (253.16, migrering 0934).
 *
 * Atle: «Koblingen gjelder mellom Truls og Siri.» En kontaktforespørsel går fra én person til én
 * person, med konteksten den kom fra (organisasjonen, objektet og rollen, 105). Godtas den, ligger
 * de i hverandres «Mine kontakter» (14), og bare de to ser hverandres egne opplysninger gjennom den.
 */
import { z } from 'zod';

import { cursorQuerySchema, page } from '../http/pagination.js';

/**
 * Statusen sett fra den som spør. `requested` står også når personen har avslått — et avslag er
 * ikke en beskjed, som en venneforespørsel.
 */
export const PERSON_CONNECTION_STATES = ['none', 'requested', 'incoming', 'connected'] as const;
export const personConnectionStateSchema = z.enum(PERSON_CONNECTION_STATES);
export type PersonConnectionState = z.infer<typeof personConnectionStateSchema>;

/** Koblingen mellom meg og én person, eller ingenting. */
export const myConnectionDtoSchema = z
  .object({ id: z.uuid().nullable(), state: personConnectionStateSchema })
  .meta({ id: 'MyConnection' });
export type MyConnectionDto = z.infer<typeof myConnectionDtoSchema>;

/** Hva forespørselen gjelder. Organisasjonen settes alltid av serveren: huset den som spør står i. */
export const personConnectionContextSchema = z
  .object({
    objectType: z.string().trim().min(1).max(40).optional(),
    objectId: z.uuid().optional(),
    objectLabel: z.string().trim().min(1).max(200).optional(),
    role: z.string().trim().min(1).max(120).optional(),
    /** Husets kontaktrad forespørselen kom fra; den kobles til personen når hen godtar. */
    contactId: z.uuid().optional(),
  })
  .meta({ id: 'PersonConnectionContext' });
export type PersonConnectionContext = z.infer<typeof personConnectionContextSchema>;

/**
 * Be om en kobling: på en person (fra en tråd, en sak eller kontaktkortet) eller på e-post («Ny
 * kontakt»). Akkurat én av dem.
 */
export const requestPersonConnectionRequestSchema = z
  .object({
    personId: z.uuid().optional(),
    email: z.email().max(320).optional(),
    context: personConnectionContextSchema.optional(),
  })
  .refine((value) => (value.personId === undefined) !== (value.email === undefined), {
    message: 'Give exactly one of personId and email.',
  })
  .meta({ id: 'RequestPersonConnectionRequest' });
export type RequestPersonConnectionRequest = z.infer<typeof requestPersonConnectionRequestSchema>;

/**
 * Svaret. På e-post er det ALLTID `sent` (8b, 254.2): det røper ikke om adressen finnes. På en
 * person er `connection` statusen etterpå — `connected` når personen alt hadde spurt.
 */
export const requestPersonConnectionResponseSchema = z
  .object({
    outcome: z.enum(['sent', 'connected']),
    connection: myConnectionDtoSchema.nullable(),
  })
  .meta({ id: 'RequestPersonConnectionResponse' });
export type RequestPersonConnectionResponse = z.infer<typeof requestPersonConnectionResponseSchema>;

/** En forespørsel til meg (15, 105, 21): hvem som spør, fra hvor, og hva det gjelder. */
export const personConnectionRequestDtoSchema = z
  .object({
    id: z.uuid(),
    requestedByPersonId: z.uuid(),
    requestedByName: z.string(),
    organizationName: z.string().nullable(),
    objectType: z.string().nullable(),
    objectLabel: z.string().nullable(),
    role: z.string().nullable(),
    requestedAt: z.iso.datetime(),
  })
  .meta({ id: 'PersonConnectionRequest' });
export type PersonConnectionRequestDto = z.infer<typeof personConnectionRequestDtoSchema>;

/** En gammel organisasjonskobling til meg som venter, med det den gjaldt (105). */
export const personLinkRequestDtoSchema = z
  .object({
    id: z.uuid(),
    requestedByName: z.string(),
    organizationName: z.string(),
    artistName: z.string().nullable(),
    role: z.string().nullable(),
    kind: z.enum(['band', 'management']).nullable(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'PersonLinkRequest' });
export type PersonLinkRequestDto = z.infer<typeof personLinkRequestDtoSchema>;

export const myConnectionRequestsResponseSchema = z
  .object({
    items: z.array(personConnectionRequestDtoSchema),
    /** De gamle forespørslene om organisasjonskobling, til de er besvart. */
    linkRequests: z.array(personLinkRequestDtoSchema),
  })
  .meta({ id: 'MyConnectionRequestsResponse' });
export type MyConnectionRequestsResponse = z.infer<typeof myConnectionRequestsResponseSchema>;

/**
 * FORESPØRSELEN SOM ET KORT I EN SAMTALE (15, 257.11). `POST /v1/me/connection-requests/conversations`
 * gir hver ventende forespørsel til meg en samtale i organisasjonen jeg står i, med kortet i, og
 * svarer med hvor den står. `threadId` er null når rollen min i organisasjonen ikke kan ha en egen
 * samtale (Begrenset); da tegnes kortet uten samtale, i Innboks.
 */
export const contactRequestConversationDtoSchema = z
  .object({
    requestId: z.uuid(),
    source: z.enum(['connection', 'person_link']),
    threadId: z.uuid().nullable(),
    /** Kortet, når det ikke har en samtale å stå i. Ellers står det i samtalen. */
    card: z.record(z.string(), z.union([z.string(), z.null()])).nullable(),
  })
  .meta({ id: 'ContactRequestConversation' });
export type ContactRequestConversationDto = z.infer<typeof contactRequestConversationDtoSchema>;

/**
 * HVOR SAMTALEN OM EN KONTAKTFORESPØRSEL STÅR, SETT FRA LESEREN (0970, 259.11).
 * `GET /v1/me/connection-requests/conversations/:threadId`. Den som spurte, ser «Sendt» til et ja —
 * også etter et avslag (257.7) — og får aldri koblingens id. Den som ble spurt, får id-en mens
 * forespørselen venter, og det er den Godta og Avslå går til.
 */
export const CONTACT_REQUEST_ROLES = ['asker', 'asked'] as const;
export const CONTACT_REQUEST_STATES = [
  'sent',
  'withdrawn',
  'incoming',
  'connected',
  'declined',
  'answered',
] as const;
export const contactRequestConversationStateDtoSchema = z
  .object({
    role: z.enum(CONTACT_REQUEST_ROLES),
    state: z.enum(CONTACT_REQUEST_STATES),
    connectionId: z.uuid().nullable(),
  })
  .meta({ id: 'ContactRequestConversationState' });
export type ContactRequestConversationStateDto = z.infer<
  typeof contactRequestConversationStateDtoSchema
>;

export const contactRequestConversationsResponseSchema = z
  .object({ items: z.array(contactRequestConversationDtoSchema) })
  .meta({ id: 'ContactRequestConversationsResponse' });
export type ContactRequestConversationsResponse = z.infer<
  typeof contactRequestConversationsResponseSchema
>;

/** Én rad i «Mine kontakter»: personens EGNE opplysninger, alltid oppdatert fra profilen. */
export const myContactDtoSchema = z
  .object({
    /** Radens id er personens: lista er personer, ikke koblinger. */
    id: z.uuid(),
    /** Null for en kollega uten egen kobling: raden kommer av medlemskapet (A3, 0998). */
    connectionId: z.uuid().nullable(),
    personId: z.uuid(),
    displayName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    /** Husets rad på personen i organisasjonen jeg står i — da har raden begge kildene. */
    contactId: z.uuid().nullable(),
    /** Organisasjonen forespørselen kom fra, eller organisasjonen vi er kolleger i. */
    contextOrganizationName: z.string().nullable(),
    /** Kobling godtatt, eller (for en kollega) da den siste av oss ble medlem. */
    acceptedAt: z.iso.datetime(),
    /**
     * KOLLEGER ER KONTAKTER (A3, 0998): vi har et aktivt medlemskap i samme organisasjon. Koblingen
     * er avledet av medlemskapet og forsvinner når en av oss slutter (254.3). Valgfri for testoppsett.
     */
    isColleague: z.boolean().optional(),
  })
  .meta({ id: 'MyContact' });
export type MyContactDto = z.infer<typeof myContactDtoSchema>;

export const listMyContactsQuerySchema = cursorQuerySchema(['name'] as const)
  .extend({ search: z.string().trim().max(200).optional() })
  .meta({ id: 'ListMyContactsQuery' });
export type ListMyContactsQuery = z.infer<typeof listMyContactsQuerySchema>;

export const myContactListResponseSchema = page(myContactDtoSchema, 'MyContactListResponse');
export type MyContactListResponse = z.infer<typeof myContactListResponseSchema>;

/**
 * PERSONKORTET fra en tråd eller en sak (95, 17). Navnet alltid — personen står der fra før. Telefon
 * og e-post bare når den som spør får se dem (`app.may_see_person`, 0935).
 */
export const personCardViewDtoSchema = z
  .object({
    personId: z.uuid(),
    displayName: z.string(),
    hasAccount: z.boolean(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    /** Husene personen er med i, som den som spør kan se (samme dør som kontaktkortet). */
    organizations: z.array(z.object({ id: z.uuid(), name: z.string() })),
    /** Husets kontaktrad på personen, når den finnes — da kan kortet lenke dit. */
    contactId: z.uuid().nullable(),
    connection: myConnectionDtoSchema,
    isMe: z.boolean(),
    /** Kolleger (A3, 0998): ingen forespørsel, kortet sier «Kollega». Valgfri for testoppsett. */
    isColleague: z.boolean().optional(),
  })
  .meta({ id: 'PersonCardView' });
export type PersonCardViewDto = z.infer<typeof personCardViewDtoSchema>;

/**
 * KONTAKTKORTET (17): «Hva personen gjør hos oss» og «samtalene personen er med i».
 *
 * `doings` er én linje per ting i huset: med i en årgang (kategori og rolle), i bandet eller
 * ledelsen til en artist, eller på en programpost. `threads` er samtalene personen er med i og som
 * leseren selv er med i — ikke samtaler OM kontakten.
 */
export const CONTACT_DOING_KINDS = ['edition', 'band', 'management', 'performance'] as const;
export const contactDoingDtoSchema = z
  .object({
    kind: z.enum(CONTACT_DOING_KINDS),
    label: z.string(),
    /**
     * Kategorien i årgangen (`participation_category`) eller gruppen på programposten
     * (`performance_party`) som kode — klienten oversetter. Null for band og ledelse.
     */
    code: z.string().nullable(),
    /** Rollen eller tittelen slik raden står; null når ingen er satt. */
    detail: z.string().nullable(),
    /** Når det finnes en dato å ordne etter (årgangen, programposten). */
    date: z.string().nullable(),
  })
  .meta({ id: 'ContactDoing' });
export type ContactDoingDto = z.infer<typeof contactDoingDtoSchema>;

export const contactInvolvementDtoSchema = z
  .object({
    doings: z.array(contactDoingDtoSchema),
    threads: z.array(
      z.object({ id: z.uuid(), subject: z.string(), lastMessageAt: z.iso.datetime() }),
    ),
  })
  .meta({ id: 'ContactInvolvement' });
export type ContactInvolvementDto = z.infer<typeof contactInvolvementDtoSchema>;
